import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isPortalTarget, PORTAL_PREFIX } from 'lib/helpers/viewportPath';
import getViewportBasePath from 'lib/helpers/appointment/getViewportBasePath';

const rewriteTarget = (to, base) => {
  // In the practice portal, base === '/portal' → leave every target untouched.
  if (base === PORTAL_PREFIX) return to;

  if (typeof to === 'string' && isPortalTarget(to)) {
    return base + to.slice(PORTAL_PREFIX.length);
  }
  if (
    to &&
    typeof to === 'object' &&
    typeof to.pathname === 'string' &&
    isPortalTarget(to.pathname)
  ) {
    return { ...to, pathname: base + to.pathname.slice(PORTAL_PREFIX.length) };
  }
  return to;
};

/**
 * Drop-in replacement for react-router's `useNavigate` that rewrites absolute
 * `/portal/...` targets to the practice-viewport base
 * (`/admin/practices/:id/...`) when the app is rendered inside the admin
 * viewport. In the practice portal it is a no-op, so existing `/portal/...`
 * navigations keep working unchanged.
 *
 * Use this in any shared practice component that navigates to an absolute
 * `/portal/...` path — otherwise an admin in the viewport is sent to a portal
 * route their session can't access and gets bounced to the login screen.
 *
 * Non-string targets (`navigate(-1)`) and non-portal paths pass through as-is.
 *
 * @returns {(to: string | object | number, options?: object) => void}
 */
const usePortalNavigate = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useCallback(
    (to, options) => navigate(rewriteTarget(to, getViewportBasePath(pathname)), options),
    [navigate, pathname]
  );
};

/**
 * Non-hook variant for lib/helper code that already receives a `navigate`
 * function and can't use the hook. Rewrites an absolute `/portal/...` target to
 * the viewport base using the current `window.location`; no-op in the portal.
 *
 * @param {string | object} to - navigation target (string path or location object)
 * @returns {string | object} the rewritten target
 */
export const rewritePortalPath = (to) =>
  rewriteTarget(
    to,
    getViewportBasePath(typeof window !== 'undefined' ? window.location.pathname : '')
  );

export default usePortalNavigate;
